<template>
  <div
    v-if="tier"
    v-b-tooltip.hover
    class="user-badge-container d-inline-flex align-items-center"
    :class="{ 'is-scrolled': isScrolled }"
    :title="tooltipText"
  >
    <!-- 1. BLUE TIER SVG (Level 1) -->
    <svg
      v-if="tier.name === 'Blue'"
      class="badge-svg mt-50"
      viewBox="0 0 120 100"
      width="50"
      height="41"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <!-- Blue Backplate Glow -->
        <filter
          id="blueGlow"
          x="-20%"
          y="-20%"
          width="140%"
          height="140%"
        >
          <feGaussianBlur
            stdDeviation="3"
            result="blur"
          />
          <feComposite
            in="SourceGraphic"
            in2="blur"
            operator="over"
          />
        </filter>
        <!-- Silver Metallic Gradient -->
        <linearGradient
          id="silverGrad"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop
            offset="0%"
            stop-color="#ffffff"
          />
          <stop
            offset="30%"
            stop-color="#dcdde1"
          />
          <stop
            offset="70%"
            stop-color="#7f8c8d"
          />
          <stop
            offset="100%"
            stop-color="#3f4f4f"
          />
        </linearGradient>
        <!-- Dark Silver Backplate -->
        <radialGradient
          id="darkSilverBg"
          cx="50%"
          cy="50%"
          r="50%"
        >
          <stop
            offset="0%"
            stop-color="#2c3e50"
          />
          <stop
            offset="100%"
            stop-color="#0f171e"
          />
        </radialGradient>
      </defs>

      <!-- Glow aura -->
      <circle
        cx="60"
        cy="50"
        r="42"
        fill="#00a8ff"
        opacity="0.3"
        filter="url(#blueGlow)"
      />

      <!-- Outer Silver Circle -->
      <circle
        cx="60"
        cy="50"
        r="38"
        fill="url(#silverGrad)"
      />
      <!-- Dark Inside Circular Plate -->
      <circle
        cx="60"
        cy="50"
        r="32"
        fill="url(#darkSilverBg)"
      />
      <!-- Inner Thin Silver Ring -->
      <circle
        cx="60"
        cy="50"
        r="30"
        fill="none"
        stroke="url(#silverGrad)"
        stroke-width="1.5"
        opacity="0.8"
      />

      <!-- Inset Border Star (Silver) -->
      <polygon
        points="60,27 81.87,42.89 73.51,68.61 46.49,68.61 38.13,42.89"
        fill="url(#silverGrad)"
      />

      <!-- 3D Faceted Central Star (Dark Grey) -->
      <!-- Left facets (light) -->
      <polygon
        points="60,50 53.53,41.1 60,25"
        fill="#7f8c8d"
      />
      <polygon
        points="60,50 66.47,41.1 83.77,42.27"
        fill="#7f8c8d"
      />
      <polygon
        points="60,50 70.46,53.4 74.69,70.23"
        fill="#7f8c8d"
      />
      <polygon
        points="60,50 60,61 45.31,70.23"
        fill="#7f8c8d"
      />
      <polygon
        points="60,50 49.54,53.4 36.23,42.27"
        fill="#7f8c8d"
      />

      <!-- Right facets (dark) -->
      <polygon
        points="60,50 66.47,41.1 60,25"
        fill="#535c68"
      />
      <polygon
        points="60,50 70.46,53.4 83.77,42.27"
        fill="#535c68"
      />
      <polygon
        points="60,50 60,61 74.69,70.23"
        fill="#535c68"
      />
      <polygon
        points="60,50 49.54,53.4 45.31,70.23"
        fill="#535c68"
      />
      <polygon
        points="60,50 53.53,41.1 36.23,42.27"
        fill="#535c68"
      />
    </svg>

    <!-- 2. BRONZE TIER SVG (Levels 2-5) -->
    <svg
      v-else-if="tier.name === 'Bronze'"
      class="badge-svg mt-50"
      viewBox="0 0 120 100"
      width="50"
      height="41"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter
          id="bronzeGlow"
          x="-20%"
          y="-20%"
          width="140%"
          height="140%"
        >
          <feGaussianBlur
            stdDeviation="3"
            result="blur"
          />
          <feComposite
            in="SourceGraphic"
            in2="blur"
            operator="over"
          />
        </filter>
        <!-- Bronze Metallic Gradient -->
        <linearGradient
          id="bronzeGrad"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop
            offset="0%"
            stop-color="#f3a683"
          />
          <stop
            offset="30%"
            stop-color="#cd7f32"
          />
          <stop
            offset="70%"
            stop-color="#ad5c1b"
          />
          <stop
            offset="100%"
            stop-color="#5a2c06"
          />
        </linearGradient>
        <!-- Dark Bronze Backplate -->
        <radialGradient
          id="darkBronzeBg"
          cx="50%"
          cy="50%"
          r="50%"
        >
          <stop
            offset="0%"
            stop-color="#5d3113"
          />
          <stop
            offset="100%"
            stop-color="#1c0a00"
          />
        </radialGradient>
      </defs>

      <!-- Glow aura -->
      <circle
        cx="60"
        cy="50"
        r="42"
        fill="#00a8ff"
        opacity="0.25"
        filter="url(#bronzeGlow)"
      />

      <!-- Outer Bronze Circle -->
      <circle
        cx="60"
        cy="50"
        r="38"
        fill="url(#bronzeGrad)"
      />
      <!-- Dark Inside Circular Plate -->
      <circle
        cx="60"
        cy="50"
        r="32"
        fill="url(#darkBronzeBg)"
      />
      <!-- Inner Thin Bronze Ring -->
      <circle
        cx="60"
        cy="50"
        r="30"
        fill="none"
        stroke="url(#bronzeGrad)"
        stroke-width="1.5"
        opacity="0.8"
      />

      <!-- Inset Border Star (Bronze) -->
      <polygon
        points="60,27 81.87,42.89 73.51,68.61 46.49,68.61 38.13,42.89"
        fill="url(#bronzeGrad)"
      />

      <!-- 3D Faceted Central Star (Bronze) -->
      <!-- Left facets (light) -->
      <polygon
        points="60,50 53.53,41.1 60,25"
        fill="#e58e26"
      />
      <polygon
        points="60,50 66.47,41.1 83.77,42.27"
        fill="#e58e26"
      />
      <polygon
        points="60,50 70.46,53.4 74.69,70.23"
        fill="#e58e26"
      />
      <polygon
        points="60,50 60,61 45.31,70.23"
        fill="#e58e26"
      />
      <polygon
        points="60,50 49.54,53.4 36.23,42.27"
        fill="#e58e26"
      />

      <!-- Right facets (dark) -->
      <polygon
        points="60,50 66.47,41.1 60,25"
        fill="#a0522d"
      />
      <polygon
        points="60,50 70.46,53.4 83.77,42.27"
        fill="#a0522d"
      />
      <polygon
        points="60,50 60,61 74.69,70.23"
        fill="#a0522d"
      />
      <polygon
        points="60,50 49.54,53.4 45.31,70.23"
        fill="#a0522d"
      />
      <polygon
        points="60,50 53.53,41.1 36.23,42.27"
        fill="#a0522d"
      />
    </svg>

    <!-- 3. SILVER TIER SVG (Levels 5-15) -->
    <svg
      v-else-if="tier.name === 'Silver Tier'"
      class="badge-svg mt-50"
      viewBox="0 0 120 100"
      width="50"
      height="41"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter
          id="silverGlow"
          x="-20%"
          y="-20%"
          width="140%"
          height="140%"
        >
          <feGaussianBlur
            stdDeviation="3"
            result="blur"
          />
          <feComposite
            in="SourceGraphic"
            in2="blur"
            operator="over"
          />
        </filter>
        <!-- Blue-Silver Gradient -->
        <linearGradient
          id="silverBlueGrad"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop
            offset="0%"
            stop-color="#ffffff"
          />
          <stop
            offset="35%"
            stop-color="#bdc5d0"
          />
          <stop
            offset="70%"
            stop-color="#7e8b9b"
          />
          <stop
            offset="100%"
            stop-color="#3d4957"
          />
        </linearGradient>
        <!-- Dark Silver-Blue Backplate -->
        <radialGradient
          id="darkSilverBlueBg"
          cx="50%"
          cy="50%"
          r="50%"
        >
          <stop
            offset="0%"
            stop-color="#2c3e50"
          />
          <stop
            offset="100%"
            stop-color="#0a121a"
          />
        </radialGradient>
      </defs>

      <!-- Glow aura -->
      <circle
        cx="60"
        cy="50"
        r="42"
        fill="#00a8ff"
        opacity="0.3"
        filter="url(#silverGlow)"
      />

      <!-- Outer Silver Circle -->
      <circle
        cx="60"
        cy="50"
        r="38"
        fill="url(#silverBlueGrad)"
      />
      <!-- Dark Inside Circular Plate -->
      <circle
        cx="60"
        cy="50"
        r="32"
        fill="url(#darkSilverBlueBg)"
      />
      <!-- Inner Thin Silver Ring -->
      <circle
        cx="60"
        cy="50"
        r="30"
        fill="none"
        stroke="url(#silverBlueGrad)"
        stroke-width="1.5"
        opacity="0.8"
      />

      <!-- Inset Border Star (Silver) -->
      <polygon
        points="60,27 81.87,42.89 73.51,68.61 46.49,68.61 38.13,42.89"
        fill="url(#silverBlueGrad)"
      />

      <!-- 3D Faceted Central Star (Blue-Silver) -->
      <!-- Left facets (light) -->
      <polygon
        points="60,50 53.53,41.1 60,25"
        fill="#f5f6fa"
      />
      <polygon
        points="60,50 66.47,41.1 83.77,42.27"
        fill="#f5f6fa"
      />
      <polygon
        points="60,50 70.46,53.4 74.69,70.23"
        fill="#f5f6fa"
      />
      <polygon
        points="60,50 60,61 45.31,70.23"
        fill="#f5f6fa"
      />
      <polygon
        points="60,50 49.54,53.4 36.23,42.27"
        fill="#f5f6fa"
      />

      <!-- Right facets (dark) -->
      <polygon
        points="60,50 66.47,41.1 60,25"
        fill="#718093"
      />
      <polygon
        points="60,50 70.46,53.4 83.77,42.27"
        fill="#718093"
      />
      <polygon
        points="60,50 60,61 74.69,70.23"
        fill="#718093"
      />
      <polygon
        points="60,50 49.54,53.4 45.31,70.23"
        fill="#718093"
      />
      <polygon
        points="60,50 53.53,41.1 36.23,42.27"
        fill="#718093"
      />
    </svg>

    <!-- 4. GOLD TIER SVG (Levels 15-30) - WINGS & SHIELD -->
    <svg
      v-else-if="tier.name === 'Gold'"
      class="badge-svg mt-50 mr-50"
      viewBox="0 0 120 100"
      width="53"
      height="50"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter
          id="goldGlow"
          x="-20%"
          y="-20%"
          width="140%"
          height="140%"
        >
          <feGaussianBlur
            stdDeviation="3"
            result="blur"
          />
          <feComposite
            in="SourceGraphic"
            in2="blur"
            operator="over"
          />
        </filter>
        <!-- Gold Highlight Gradient -->
        <linearGradient
          id="goldMetal"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop
            offset="0%"
            stop-color="#ffeaa7"
          />
          <stop
            offset="30%"
            stop-color="#fdcb6e"
          />
          <stop
            offset="70%"
            stop-color="#e1b12c"
          />
          <stop
            offset="100%"
            stop-color="#ad7f07"
          />
        </linearGradient>
        <!-- Silver Wings Gradient -->
        <linearGradient
          id="silverWings"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop
            offset="0%"
            stop-color="#ffffff"
          />
          <stop
            offset="50%"
            stop-color="#bdc3c7"
          />
          <stop
            offset="100%"
            stop-color="#7f8c8d"
          />
        </linearGradient>
        <!-- Dark Gold Center Plate -->
        <radialGradient
          id="darkGoldBg"
          cx="50%"
          cy="50%"
          r="50%"
        >
          <stop
            offset="0%"
            stop-color="#0f2038"
          />
          <stop
            offset="100%"
            stop-color="#050a12"
          />
        </radialGradient>
      </defs>

      <!-- Glow aura -->
      <circle
        cx="60"
        cy="50"
        r="42"
        fill="#00a8ff"
        opacity="0.35"
        filter="url(#goldGlow)"
      />

      <!-- Left Wings -->
      <path
        d="M 28 50 C 10 45 8 30 14 15 C 22 25 24 38 28 45 Z"
        fill="url(#silverWings)"
      />
      <path
        d="M 26 62 C 6 60 4 45 10 32 C 18 40 20 52 26 57 Z"
        fill="url(#silverWings)"
        opacity="0.9"
      />
      <path
        d="M 28 72 C 12 75 8 60 12 48 C 18 55 22 65 28 68 Z"
        fill="url(#silverWings)"
        opacity="0.75"
      />

      <!-- Right Wings -->
      <path
        d="M 92 50 C 110 45 112 30 106 15 C 98 25 96 38 92 45 Z"
        fill="url(#silverWings)"
      />
      <path
        d="M 94 62 C 114 60 116 45 110 32 C 102 40 100 52 94 57 Z"
        fill="url(#silverWings)"
        opacity="0.9"
      />
      <path
        d="M 92 72 C 108 75 112 60 108 48 C 102 55 98 65 92 68 Z"
        fill="url(#silverWings)"
        opacity="0.75"
      />

      <!-- Bottom Shield/Ribbon -->
      <path
        d="M 46 76 L 60 92 L 74 76 L 68 74 L 52 74 Z"
        fill="url(#silverWings)"
        stroke="#7f8c8d"
        stroke-width="1"
      />

      <!-- Outer Gold Circle -->
      <circle
        cx="60"
        cy="50"
        r="36"
        fill="url(#goldMetal)"
      />
      <!-- Dark Inside Circular Plate -->
      <circle
        cx="60"
        cy="50"
        r="30"
        fill="url(#darkGoldBg)"
      />
      <!-- Inner Thin Gold Ring -->
      <circle
        cx="60"
        cy="50"
        r="28"
        fill="none"
        stroke="url(#goldMetal)"
        stroke-width="1.5"
        opacity="0.8"
      />

      <!-- Inset Border Star (Gold) -->
      <polygon
        points="60,29 78.43,42.5 71.38,64.3 48.62,64.3 41.57,42.5"
        fill="url(#goldMetal)"
      />

      <!-- 3D Faceted Central Star (Gold & Blue Facets) -->
      <!-- Left facets (light gold) -->
      <polygon
        points="60,50 54.8,42.9 60,30"
        fill="#f1c40f"
      />
      <polygon
        points="60,50 65.2,42.9 79,43.8"
        fill="#f1c40f"
      />
      <polygon
        points="60,50 68.4,52.7 71.8,66.2"
        fill="#f1c40f"
      />
      <polygon
        points="60,50 60,58.8 48.2,66.2"
        fill="#f1c40f"
      />
      <polygon
        points="60,50 51.6,52.7 41,43.8"
        fill="#f1c40f"
      />

      <!-- Right facets (dark orange-gold or blue accents) -->
      <polygon
        points="60,50 65.2,42.9 60,30"
        fill="#d35400"
      />
      <polygon
        points="60,50 68.4,52.7 79,43.8"
        fill="#2980b9"
      /> <!-- Blue Accent facet -->
      <polygon
        points="60,50 60,58.8 71.8,66.2"
        fill="#d35400"
      />
      <polygon
        points="60,50 51.6,52.7 48.2,66.2"
        fill="#2980b9"
      /> <!-- Blue Accent facet -->
      <polygon
        points="60,50 54.8,42.9 41,43.8"
        fill="#d35400"
      />
    </svg>

    <!-- 5. DIAMOND TIER SVG (Levels 30+) - GOLD WINGS, RED STAR & GOLD SHIELD -->
    <svg
      v-else-if="tier.name === 'Diamond'"
      class="badge-svg mt-50 mr-50 animate-diamond-glow"
      viewBox="0 0 120 100"
      width="53"
      height="50"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter
          id="diamondGlow"
          x="-25%"
          y="-25%"
          width="150%"
          height="150%"
        >
          <feGaussianBlur
            stdDeviation="4"
            result="blur"
          />
          <feComposite
            in="SourceGraphic"
            in2="blur"
            operator="over"
          />
        </filter>
        <!-- Premium Gold Metallic Gradient -->
        <linearGradient
          id="goldWingsGrad"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop
            offset="0%"
            stop-color="#ffeaa7"
          />
          <stop
            offset="25%"
            stop-color="#fdcb6e"
          />
          <stop
            offset="50%"
            stop-color="#f1c40f"
          />
          <stop
            offset="75%"
            stop-color="#e67e22"
          />
          <stop
            offset="100%"
            stop-color="#ad7f07"
          />
        </linearGradient>
        <!-- Hot Red Backplate -->
        <radialGradient
          id="redCenterBg"
          cx="50%"
          cy="50%"
          r="50%"
        >
          <stop
            offset="0%"
            stop-color="#c23616"
          />
          <stop
            offset="70%"
            stop-color="#7f0c00"
          />
          <stop
            offset="100%"
            stop-color="#2c0000"
          />
        </radialGradient>
        <!-- Ruby Red Star Gradient -->
        <linearGradient
          id="rubyGrad"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop
            offset="0%"
            stop-color="#ff7675"
          />
          <stop
            offset="100%"
            stop-color="#d63031"
          />
        </linearGradient>
      </defs>

      <!-- Glow aura (Yellow/Reddish Gold) -->
      <circle
        cx="60"
        cy="50"
        r="42"
        fill="#ff7675"
        opacity="0.4"
        filter="url(#diamondGlow)"
      />

      <!-- Left Gold Wings -->
      <path
        d="M 28 50 C 10 45 8 30 14 15 C 22 25 24 38 28 45 Z"
        fill="url(#goldWingsGrad)"
      />
      <path
        d="M 26 62 C 6 60 4 45 10 32 C 18 40 20 52 26 57 Z"
        fill="url(#goldWingsGrad)"
        opacity="0.9"
      />
      <path
        d="M 28 72 C 12 75 8 60 12 48 C 18 55 22 65 28 68 Z"
        fill="url(#goldWingsGrad)"
        opacity="0.75"
      />

      <!-- Right Gold Wings -->
      <path
        d="M 92 50 C 110 45 112 30 106 15 C 98 25 96 38 92 45 Z"
        fill="url(#goldWingsGrad)"
      />
      <path
        d="M 94 62 C 114 60 116 45 110 32 C 102 40 100 52 94 57 Z"
        fill="url(#goldWingsGrad)"
        opacity="0.9"
      />
      <path
        d="M 92 72 C 108 75 112 60 108 48 C 102 55 98 65 92 68 Z"
        fill="url(#goldWingsGrad)"
        opacity="0.75"
      />

      <!-- Bottom Shield/Ribbon (Gold) -->
      <path
        d="M 46 76 L 60 92 L 74 76 L 68 74 L 52 74 Z"
        fill="url(#goldWingsGrad)"
        stroke="#b38728"
        stroke-width="1"
      />

      <!-- Outer Gold Circle -->
      <circle
        cx="60"
        cy="50"
        r="36"
        fill="url(#goldWingsGrad)"
      />
      <!-- Dark Red Inside Circular Plate -->
      <circle
        cx="60"
        cy="50"
        r="30"
        fill="url(#redCenterBg)"
      />
      <!-- Inner Thin Gold Ring -->
      <circle
        cx="60"
        cy="50"
        r="28"
        fill="none"
        stroke="url(#goldWingsGrad)"
        stroke-width="1.5"
        opacity="0.85"
      />

      <!-- Inset Border Star (Gold Outline) -->
      <polygon
        points="60,29 78.43,42.5 71.38,64.3 48.62,64.3 41.57,42.5"
        fill="url(#goldWingsGrad)"
      />

      <!-- 3D Faceted Central Star (Gold & Ruby Red Facets) -->
      <!-- Left facets (light ruby red / gold border) -->
      <polygon
        points="60,50 54.8,42.9 60,30"
        fill="#ffeaa7"
      /> <!-- Gold facet -->
      <polygon
        points="60,50 65.2,42.9 79,43.8"
        fill="#d63031"
      /> <!-- Red facet -->
      <polygon
        points="60,50 68.4,52.7 71.8,66.2"
        fill="#ffeaa7"
      /> <!-- Gold facet -->
      <polygon
        points="60,50 60,58.8 48.2,66.2"
        fill="#d63031"
      /> <!-- Red facet -->
      <polygon
        points="60,50 51.6,52.7 41,43.8"
        fill="#ffeaa7"
      /> <!-- Gold facet -->

      <!-- Right facets (dark red / orange-red) -->
      <polygon
        points="60,50 65.2,42.9 60,30"
        fill="#d35400"
      />
      <polygon
        points="60,50 68.4,52.7 79,43.8"
        fill="#9f1c1c"
      />
      <polygon
        points="60,50 60,58.8 71.8,66.2"
        fill="#d35400"
      />
      <polygon
        points="60,50 51.6,52.7 48.2,66.2"
        fill="#9f1c1c"
      />
      <polygon
        points="60,50 54.8,42.9 41,43.8"
        fill="#d35400"
      />
    </svg>

    <!-- Labels beside the badge -->
    <div class="d-flex flex-column text-left badge-labels mr-2 mt-50">
      <span
        class="badge-tier-name font-weight-bolder text-uppercase"
        :style="{ color: tier.labelColor, fontSize: '0.88rem', lineHeight: '1.2', letterSpacing: '0.5px' }"
      >
        {{ tier.name }}
      </span>
      <span
        class="badge-level-num text-muted font-weight-bold"
        style="font-size: 0.8rem; line-height: 1.1;"
      >
        Level {{ level }}
      </span>
    </div>
  </div>
</template>

<script>
import { VBTooltip } from 'bootstrap-vue'

export default {
  name: 'UserBadge',
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      isScrolled: false,
      localUserData: JSON.parse(localStorage.getItem('userData') || '{}'),
    }
  },
  computed: {
    level() {
      // return this.localUserData.level !== undefined ? Number(this.localUserData.level) : 6
      return 10
    },
    tier() {
      const val = this.level
      if (val <= 1) {
        return {
          name: 'Blue',
          class: 'tier-blue',
          labelColor: '#0984e3',
        }
      }
      if (val >= 2 && val <= 5) {
        return {
          name: 'Bronze',
          class: 'tier-bronze',
          labelColor: '#cd7f32',
        }
      }
      if (val > 5 && val <= 15) {
        return {
          name: 'Silver Tier',
          class: 'tier-silver',
          labelColor: '#8a99ad',
        }
      }
      if (val > 15 && val <= 30) {
        return {
          name: 'Gold',
          class: 'tier-gold',
          labelColor: '#e1b12c',
        }
      }
      // val > 30
      return {
        name: 'Diamond',
        class: 'tier-diamond',
        labelColor: '#00d2ff',
      }
    },
    tooltipText() {
      const val = this.level
      if (val >= 31) {
        return `Level ${val} (${this.tier.name} Tier) - Maximum tier reached!`
      }

      let nextTier = ''
      let reqLevel = 0

      if (val <= 1) {
        nextTier = 'Bronze'
        reqLevel = 2
      } else if (val >= 2 && val <= 5) {
        nextTier = 'Silver'
        reqLevel = 6
      } else if (val >= 6 && val <= 15) {
        nextTier = 'Gold'
        reqLevel = 16
      } else if (val >= 16 && val <= 30) {
        nextTier = 'Diamond'
        reqLevel = 31
      }

      const remaining = reqLevel - val
      const levelWord = remaining === 1 ? 'level' : 'levels'
      return `Level ${val} (${this.tier.name} Tier) • ${remaining} ${levelWord} remaining to reach ${nextTier} Tier`
    },
  },
  mounted() {
    window.addEventListener('storageUpdate', this.handleStorageUpdate)
    window.addEventListener('storage', this.handleStorageChange)
    window.addEventListener('scroll', this.handleScroll, true)
    // Run once at start in case already scrolled
    this.handleScroll()
  },
  beforeDestroy() {
    window.removeEventListener('storageUpdate', this.handleStorageUpdate)
    window.removeEventListener('storage', this.handleStorageChange)
    window.removeEventListener('scroll', this.handleScroll, true)
  },
  methods: {
    handleScroll(event) {
      let scrollTop = 0
      if (event && event.target) {
        if (event.target === document || event.target === window) {
          scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0
        } else if (event.target.scrollTop !== undefined) {
          scrollTop = event.target.scrollTop
        }
      } else {
        scrollTop = window.scrollY || document.documentElement.scrollTop || 0
      }
      this.isScrolled = scrollTop > 20
    },
    handleStorageUpdate(event) {
      if (event.detail && event.detail.key === 'userData') {
        try {
          this.localUserData = JSON.parse(event.detail.value || '{}')
        } catch (e) {
          console.error('Failed to parse userData from custom event', e)
        }
      }
    },
    handleStorageChange(event) {
      if (event.key === 'userData') {
        try {
          this.localUserData = JSON.parse(event.newValue || '{}')
        } catch (e) {
          console.error('Failed to parse userData from storage event', e)
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
.user-badge-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  transition: opacity 0.3s ease, transform 0.3s ease, background-color 0.25s ease;

  // Reset top margin to align perfectly inside the fixed capsule
  .badge-svg,
  .badge-labels {
    margin-top: 0 !important;
  }

  // Hide the badge when scrolled down
  &.is-scrolled {
    opacity: 0;
    pointer-events: none;
    transform: translateY(-20px);
  }

}

.badge-svg {
  display: block;
  user-select: none;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
  transition: transform 0.25s ease, filter 0.25s ease;

  &:hover {
    transform: translateY(-2px) scale(1.05);
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  }
}

/* Specific glowing animations for Diamond */
.animate-diamond-glow {
  animation: gold-pulse 3s infinite ease-in-out;
}

@keyframes gold-pulse {
  0% {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15)) drop-shadow(0 0 2px rgba(253, 203, 110, 0.2));
  }
  50% {
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.25)) drop-shadow(0 0 8px rgba(253, 203, 110, 0.5));
  }
  100% {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15)) drop-shadow(0 0 2px rgba(253, 203, 110, 0.2));
  }
}
</style>
